/**
 * Stage 3: choose the primary rectangle - the pair of opposing wall lines a
 * human would put a tape measure against.
 *
 * Every combination of two vertical and two horizontal wall lines is a
 * candidate. A candidate is *legal* if each piece of room outside it attaches
 * squarely to one side (an alcove in a "plus" cell of the 3×3 grid - never a
 * diagonal cell). Legal candidates are scored:
 *
 *   + wall length lying exactly ON the rectangle's sides  (tape-measure walls)
 *   + area of room covered
 *   − area inside the rectangle but outside the room, weighted by how well the
 *     model can express it: corner-chamfer triangles are nearly free, wall-like
 *     rectangles cheap (free wall elements), anything else expensive.
 */

import { Point } from '../schemas';
import { BBox, bboxOf, CleanWall, differenceComponents, intersectArea, rectRingPts, simplifyRing } from './rawGeometry';

export interface PrimaryRect {
  xL: number;
  xR: number;
  yF: number;
  yB: number;
}

export interface ResidualComponent {
  ring: Point[];
  area: number;
  bbox: BBox;
}

export interface PrimaryRectChoice {
  rect: PrimaryRect;
  /** Room outside the rect (→ alcoves), keyed by the raw-frame side it attaches to. */
  protrusions: { side: 'west' | 'north' | 'east' | 'south'; comp: ResidualComponent }[];
  /** Rect outside the room (→ corner chamfers / free wall elements). */
  intrusions: { kind: 'cornerTriangle' | 'wallRect' | 'other'; comp: ResidualComponent }[];
}

const W_SUPPORT = 1.0, W_AREA = 0.25, W_CHAMFER = 0.05, W_WALL_RECT = 0.5, W_OTHER = 2.0;
const EPS = 0.02;

/** Total wall length lying on an axis line within a span (synthetic bridges don't count). */
function supportOn(walls: CleanWall[], axis: 'h' | 'v', line: number, lo: number, hi: number): number {
  let sum = 0;
  for (const w of walls) {
    if (w.axis !== axis || w.synthetic) continue;
    const coord = axis === 'h' ? w.a.y : w.a.x;
    if (Math.abs(coord - line) > EPS) continue;
    const [a, b] = axis === 'h' ? [w.a.x, w.b.x] : [w.a.y, w.b.y];
    sum += Math.max(0, Math.min(hi, Math.max(a, b)) - Math.max(lo, Math.min(a, b)));
  }
  return sum;
}

/** Which rect side a protrusion attaches to, or null if it lands in a diagonal cell. */
function protrusionSide(bbox: BBox, r: PrimaryRect): 'west' | 'north' | 'east' | 'south' | null {
  const spansX = bbox.x0 >= r.xL - EPS && bbox.x1 <= r.xR + EPS;
  const spansY = bbox.y0 >= r.yF - EPS && bbox.y1 <= r.yB + EPS;
  if (Math.abs(bbox.x1 - r.xL) < EPS && spansY) return 'west';
  if (Math.abs(bbox.x0 - r.xR) < EPS && spansY) return 'east';
  if (Math.abs(bbox.y1 - r.yF) < EPS && spansX) return 'south';
  if (Math.abs(bbox.y0 - r.yB) < EPS && spansX) return 'north';
  return null;
}

function classifyIntrusion(comp: ResidualComponent, r: PrimaryRect): 'cornerTriangle' | 'wallRect' | 'other' {
  const ring = simplifyRing(comp.ring, EPS);
  const { bbox } = comp;
  const corners = [
    { x: r.xL, y: r.yF },
    { x: r.xR, y: r.yF },
    { x: r.xR, y: r.yB },
    { x: r.xL, y: r.yB },
  ];
  const touchesCorner = corners.some(
    c =>
      (Math.abs(bbox.x0 - c.x) < EPS || Math.abs(bbox.x1 - c.x) < EPS)
      && (Math.abs(bbox.y0 - c.y) < EPS || Math.abs(bbox.y1 - c.y) < EPS),
  );
  if (ring.length === 3 && touchesCorner) return 'cornerTriangle';
  const bboxArea = (bbox.x1 - bbox.x0) * (bbox.y1 - bbox.y0);
  if (comp.area > 0.9 * bboxArea) return 'wallRect';
  return 'other';
}

/** The pieces of `a` outside `b`, each with its bounding box; crumbs under 0.05 ft² are dropped. */
const residualPieces = (a: Point[], b: Point[]): ResidualComponent[] =>
  differenceComponents(a, b).filter(comp => !(comp.area < 0.05)).map(comp => ({ ...comp, bbox: bboxOf(comp.ring) }));

/** The room outside the rect, each piece with the side it attaches to; null if any lands in a diagonal cell. */
function protrusionsOf(roomRing: Point[], rectRing: Point[], r: PrimaryRect): PrimaryRectChoice['protrusions'] | null {
  const protrusions: PrimaryRectChoice['protrusions'] = [];
  for (const comp of residualPieces(roomRing, rectRing)) {
    const side = protrusionSide(comp.bbox, r);
    if (!side) return null;
    protrusions.push({ side, comp });
  }
  return protrusions;
}

/** A piece thinner than ~4" is scan noise (jitter between nearly-collinear walls): modeling it as a chamfer or wall just corrupts the geometry. */
const isSliver = ({ bbox, area }: ResidualComponent): boolean => Math.min(bbox.x1 - bbox.x0, bbox.y1 - bbox.y0) < 0.33 && area < 3;

/** The rect outside the room, slivers aside, each piece classified. */
const intrusionsOf = (rectRing: Point[], roomRing: Point[], r: PrimaryRect): PrimaryRectChoice['intrusions'] =>
  residualPieces(rectRing, roomRing).filter(comp => !isSliver(comp)).map(comp => ({ kind: classifyIntrusion(comp, r), comp }));

const INTRUSION_WEIGHTS = { cornerTriangle: W_CHAMFER, wallRect: W_WALL_RECT, other: W_OTHER };

/** What the intrusions cost: each piece's area, weighted by how well the model expresses it. */
const intrusionCost = (intrusions: PrimaryRectChoice['intrusions']): number =>
  intrusions.reduce((cost, { kind, comp }) => cost + comp.area * INTRUSION_WEIGHTS[kind], 0);

/** Wall length lying on the rect's four sides. */
const wallSupport = (walls: CleanWall[], r: PrimaryRect): number =>
  supportOn(walls, 'v', r.xL, r.yF, r.yB) + supportOn(walls, 'v', r.xR, r.yF, r.yB)
  + supportOn(walls, 'h', r.yF, r.xL, r.xR) + supportOn(walls, 'h', r.yB, r.xL, r.xR);

/** Evaluate one candidate; null if illegal (diagonal-cell protrusion or degenerate). */
function evaluate(r: PrimaryRect, roomRing: Point[], walls: CleanWall[]): { score: number; choice: PrimaryRectChoice } | null {
  if (r.xR - r.xL < 1 || r.yB - r.yF < 1) return null;
  const rectRing = rectRingPts({ x0: r.xL, y0: r.yF, x1: r.xR, y1: r.yB });
  const rectArea = (r.xR - r.xL) * (r.yB - r.yF);
  const inter = intersectArea(roomRing, rectRing);
  if (inter < 0.5 * rectArea) return null;
  const protrusions = protrusionsOf(roomRing, rectRing, r);
  if (!protrusions) return null;
  const intrusions = intrusionsOf(rectRing, roomRing, r);
  const score = W_SUPPORT * wallSupport(walls, r) + W_AREA * inter - intrusionCost(intrusions);
  return { score, choice: { rect: r, protrusions, intrusions } };
}

/** Pick the best primary rectangle for a perimeter loop. */
export function choosePrimaryRect(roomRing: Point[], walls: CleanWall[]): PrimaryRectChoice {
  const xs = [...new Set(walls.filter(w => w.axis === 'v').map(w => w.a.x))].sort((a, b) => a - b);
  const ys = [...new Set(walls.filter(w => w.axis === 'h').map(w => w.a.y))].sort((a, b) => a - b);

  let best: { score: number; choice: PrimaryRectChoice } | null = null;
  for (let i = 0; i < xs.length; i++) {
    for (let j = i + 1; j < xs.length; j++) {
      for (let k = 0; k < ys.length; k++) {
        for (let l = k + 1; l < ys.length; l++) {
          const r = evaluate({ xL: xs[i], xR: xs[j], yF: ys[k], yB: ys[l] }, roomRing, walls);
          if (r && (!best || r.score > best.score)) best = r;
        }
      }
    }
  }
  if (!best) throw new Error('No legal primary rectangle found');
  return best.choice;
}
